import { useState } from 'react';
import { Card, Input, Button, Space, Tag, Typography, App } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useUpsertUiKey } from '@/hooks/useUiTranslations';
import { missingPlaceholders } from '@/lib/placeholders';
import { AiSuggestButton } from './AiSuggestButton';

const { Title, Paragraph, Text } = Typography;

interface Props {
  targetLang: string;
  sourceLang: string;
  selectedKey: string | null;
  sourceValue: string;
  targetValue: string;
  zhRef?: string;
}

export function KeyEditorColumn({
  targetLang,
  sourceLang,
  selectedKey,
  sourceValue,
  targetValue,
  zhRef,
}: Props) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState(targetValue);
  // Track last synced (key, server value) in state — React's documented pattern for
  // deriving state from props without an effect. Sync when either changes.
  const [lastSync, setLastSync] = useState<{ key: string | null; value: string }>({
    key: selectedKey,
    value: targetValue,
  });
  const upsert = useUpsertUiKey();

  if (lastSync.key !== selectedKey || lastSync.value !== targetValue) {
    setLastSync({ key: selectedKey, value: targetValue });
    setDraft(targetValue);
  }

  const missing = missingPlaceholders(sourceValue, draft);

  async function save() {
    if (!selectedKey) return;
    try {
      await upsert.mutateAsync({ lang: targetLang, key: selectedKey, value: draft });
      message.success('Saved');
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  if (!selectedKey) {
    return (
      <Card>
        <Text type="secondary">Select a key on the left to edit.</Text>
      </Card>
    );
  }

  return (
    <Card
      title={<code style={{ fontSize: 14 }}>{selectedKey}</code>}
      extra={
        <Space>
          <AiSuggestButton
            sourceLang={sourceLang}
            targetLang={targetLang}
            sourceText={sourceValue}
            onSuggested={setDraft}
          />
          <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={upsert.isPending}>
            Save
          </Button>
        </Space>
      }
    >
      <Title level={5} style={{ marginTop: 0 }}>
        Target ({targetLang})
      </Title>
      <Input.TextArea
        rows={5}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        autoFocus
      />
      {missing.length > 0 && (
        <Paragraph style={{ marginTop: 8 }}>
          {missing.map((p) => (
            <Tag color="warning" key={p}>
              missing placeholder: <code>{p}</code>
            </Tag>
          ))}
        </Paragraph>
      )}

      <Title level={5} style={{ marginTop: 16 }}>
        English (read-only)
      </Title>
      <Input.TextArea rows={3} value={sourceValue} readOnly style={{ background: '#fafafa' }} />

      {zhRef !== undefined && targetLang !== 'zh-Hans' && (
        <>
          <Title level={5} style={{ marginTop: 16 }}>
            简体中文 (read-only)
          </Title>
          <Input.TextArea rows={3} value={zhRef} readOnly style={{ background: '#fafafa' }} />
        </>
      )}
    </Card>
  );
}
